import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';
import type { PagePermissions } from '../../../utils/permissions';

const SESSION_ID_STORAGE_KEY = 'session_id';
const LAST_LOGIN_STORAGE_KEY = 'last_login';
const TAB_ID_STORAGE_KEY = 'auth_tab_id';
// "Remember me" seçimi; işaretliyse hareketsizlik çıkışı uygulanmaz (InactivityWatcher)
export const REMEMBER_STORAGE_KEY = 'auth_remember';
const TAB_REGISTRY_PREFIX = 'auth_session_tabs:';
const TAB_REGISTRY_TTL_MS = 2 * 60 * 1000;
const SESSION_HEARTBEAT_MS = 60 * 1000;
// İzinler bu aralıkla /auth/me üzerinden tazelenir (yeniden login gerekmez)
const PERMISSION_REFRESH_MS = 5 * 60 * 1000;

interface AuthContextType {
  user: string | null;
  permitted_pages: string[];
  /** Aksiyon bazlı izinler: { "/path": ["read","edit",...], "*": [...] } */
  page_permissions: PagePermissions;
  role: string | null;
  token: string | null;
  personnelId: string | null;
  lastLogin: number | null;
  sessionId: string | null;
  login: (username: string, password: string, remember?: boolean) => Promise<boolean>;
  logout: () => void;
  /** İzinleri /auth/me'den anında tazele (ör. 403 sonrası) */
  refreshPermissions: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Helper function to get API base URL
const getApiBaseUrl = () => {
  // Check for environment variable first
  const envUrl = process.env.REACT_APP_API_URL;
  if (envUrl && envUrl.trim()) {
    return envUrl.trim();
  }
  // For localhost development, use direct backend URL
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'http://localhost:5000/api';
  }
  // For production/VPS, use relative URL (nginx proxies to backend)
  return '/api';
};

const getOrCreateTabId = () => {
  const existing = sessionStorage.getItem(TAB_ID_STORAGE_KEY);
  if (existing) {
    return existing;
  }

  const nextId =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `tab-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  sessionStorage.setItem(TAB_ID_STORAGE_KEY, nextId);
  return nextId;
};

const getTabRegistryKey = (sessionId: string) => `${TAB_REGISTRY_PREFIX}${sessionId}`;

const readTabRegistry = (sessionId: string): Record<string, number> => {
  try {
    const raw = localStorage.getItem(getTabRegistryKey(sessionId));
    const parsed = raw ? JSON.parse(raw) : {};
    if (!parsed || typeof parsed !== 'object') {
      return {};
    }

    const now = Date.now();
    const pruned = Object.entries(parsed).reduce<Record<string, number>>((acc, [tabId, ts]) => {
      if (typeof ts === 'number' && now - ts <= TAB_REGISTRY_TTL_MS) {
        acc[tabId] = ts;
      }
      return acc;
    }, {});

    return pruned;
  } catch {
    return {};
  }
};

const writeTabRegistry = (sessionId: string, registry: Record<string, number>) => {
  const key = getTabRegistryKey(sessionId);
  const entries = Object.entries(registry);
  if (entries.length === 0) {
    localStorage.removeItem(key);
    return;
  }
  localStorage.setItem(key, JSON.stringify(registry));
};

const touchCurrentTab = (sessionId: string) => {
  const tabId = getOrCreateTabId();
  const registry = readTabRegistry(sessionId);
  registry[tabId] = Date.now();
  writeTabRegistry(sessionId, registry);
};

const removeCurrentTab = (sessionId: string) => {
  const tabId = getOrCreateTabId();
  const registry = readTabRegistry(sessionId);
  delete registry[tabId];
  writeTabRegistry(sessionId, registry);
  return Object.keys(registry).length;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem('token'));
  const [user, setUser] = useState<string | null>(() => localStorage.getItem('user'));
  const [permitted_pages, setPermittedPages] = useState<string[]>(() => {
    const storedPages = localStorage.getItem('permitted_pages');
    try {
      return storedPages ? JSON.parse(storedPages) : [];
    } catch {
      return [];
    }
  });
  const [page_permissions, setPagePermissions] = useState<PagePermissions>(() => {
    const stored = localStorage.getItem('page_permissions');
    try {
      const parsed = stored ? JSON.parse(stored) : {};
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  });
  const [role, setRole] = useState<string | null>(() => localStorage.getItem('role'));
  const [personnelId, setPersonnelId] = useState<string | null>(() => localStorage.getItem('personnel_id'));
  const [lastLogin, setLastLogin] = useState<number | null>(() => {
    const stored = localStorage.getItem(LAST_LOGIN_STORAGE_KEY);
    return stored ? parseInt(stored, 10) : null;
  });
  const [sessionId, setSessionId] = useState<string | null>(() => localStorage.getItem(SESSION_ID_STORAGE_KEY));

  const clearAuthStorage = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('permitted_pages');
    localStorage.removeItem('page_permissions');
    localStorage.removeItem('role');
    localStorage.removeItem('personnel_id');
    localStorage.removeItem('user_id');
    localStorage.removeItem(LAST_LOGIN_STORAGE_KEY);
    localStorage.removeItem(SESSION_ID_STORAGE_KEY);
    localStorage.removeItem(REMEMBER_STORAGE_KEY);
  };

  // Yanıttan izin alanlarını state + localStorage'a uygular (login ve /auth/me ortak)
  const applyPermissionPayload = (data: any) => {
    const pages = Array.isArray(data.permitted_pages) ? data.permitted_pages : [];
    setPermittedPages(pages);
    localStorage.setItem('permitted_pages', JSON.stringify(pages));

    const perms =
      data.page_permissions && typeof data.page_permissions === 'object'
        ? (data.page_permissions as PagePermissions)
        : {};
    setPagePermissions(perms);
    localStorage.setItem('page_permissions', JSON.stringify(perms));

    const nextRole = typeof data.role === 'string' && data.role ? data.role : null;
    setRole(nextRole);
    if (nextRole) {
      localStorage.setItem('role', nextRole);
    } else {
      localStorage.removeItem('role');
    }
  };

  // login function expects permitted_pages as string[] from backend
  const login = async (username: string, password: string, remember: boolean = false) => {
    try {
      const baseURL = getApiBaseUrl();
      const res = await axios.post(`${baseURL}/auth/login`, { username, password, remember });
      const data = res.data;
      if (data.token) {
        if (remember) {
          localStorage.setItem(REMEMBER_STORAGE_KEY, '1');
        } else {
          localStorage.removeItem(REMEMBER_STORAGE_KEY);
        }
        setToken(data.token);
        setUser(username);
        applyPermissionPayload(data);
        setSessionId(data.session_id || null);
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', username);
        if (data.session_id) {
          localStorage.setItem(SESSION_ID_STORAGE_KEY, data.session_id);
          touchCurrentTab(data.session_id);
        } else {
          localStorage.removeItem(SESSION_ID_STORAGE_KEY);
        }
        if (data.personnel_id) {
          setPersonnelId(String(data.personnel_id));
          localStorage.setItem('personnel_id', String(data.personnel_id));
        }
        if (data.user_id) {
          localStorage.setItem('user_id', String(data.user_id));
        }
        // Update last login timestamp
        const loginTime = Date.now();
        setLastLogin(loginTime);
        localStorage.setItem(LAST_LOGIN_STORAGE_KEY, loginTime.toString());
        return true;
      }
    } catch (err) {
      // swallow and treat as failed login
    }
    setToken(null);
    setUser(null);
    setPermittedPages([]);
    setPagePermissions({});
    setRole(null);
    setPersonnelId(null);
    setLastLogin(null);
    setSessionId(null);
    clearAuthStorage();
    return false;
  };

  const performLogout = async (reason: string) => {
    const currentToken = token;
    const currentSessionId = sessionId;

    setToken(null);
    setUser(null);
    setPermittedPages([]);
    setPagePermissions({});
    setRole(null);
    setPersonnelId(null);
    setLastLogin(null);
    setSessionId(null);
    clearAuthStorage();

    if (currentSessionId) {
      removeCurrentTab(currentSessionId);
      localStorage.removeItem(getTabRegistryKey(currentSessionId));
    }

    if (!currentToken) {
      return;
    }

    try {
      const baseURL = getApiBaseUrl();
      await axios
        .post(
          `${baseURL}/auth/logout`,
          {
            session_id: currentSessionId,
            reason,
          },
          { headers: { Authorization: `Bearer ${currentToken}` } }
        )
        .catch(() => {});
    } catch {
      // Ignore logout errors
    }
  };

  const logout = () => {
    void performLogout('manual');
  };

  useEffect(() => {
    if (!token || !sessionId) {
      return;
    }

    const baseURL = getApiBaseUrl();
    touchCurrentTab(sessionId);

    const sendHeartbeat = async () => {
      touchCurrentTab(sessionId);
      try {
        await axios.post(
          `${baseURL}/auth/session/ping`,
          { session_id: sessionId },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch {
        // Ignore ping failures; auth interceptor will handle hard 401s elsewhere.
      }
    };

    void sendHeartbeat();
    const heartbeatTimer = window.setInterval(() => {
      void sendHeartbeat();
    }, SESSION_HEARTBEAT_MS);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void sendHeartbeat();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.clearInterval(heartbeatTimer);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [token, sessionId]);

  // İzinleri /auth/me'den tazele — login gerektirmeden güncel yetkiler alınır
  const refreshPermissions = async () => {
    const currentToken = token || localStorage.getItem('token');
    if (!currentToken) return;
    try {
      const baseURL = getApiBaseUrl();
      const res = await axios.get(`${baseURL}/auth/me`, {
        headers: { Authorization: `Bearer ${currentToken}` },
      });
      if (res.data.username) {
        setUser(res.data.username);
        localStorage.setItem('user', res.data.username);
      }
      applyPermissionPayload(res.data);
    } catch (err: any) {
      // 401/403: oturum ya da hesap geçersiz — çıkışa zorla
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        logout();
      }
      // Ağ hatalarında mevcut (cache) izinlerle devam edilir
    }
  };

  useEffect(() => {
    if (!token) return;
    void refreshPermissions();
    const timer = window.setInterval(() => {
      void refreshPermissions();
    }, PERMISSION_REFRESH_MS);
    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <AuthContext.Provider
      value={{
        user,
        permitted_pages,
        page_permissions,
        role,
        token,
        personnelId,
        lastLogin,
        sessionId,
        login,
        logout,
        refreshPermissions,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
